import { groupBy, last, sortBy } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useToggle } from "react-use";
import { StringParam, useQueryParams } from "use-query-params";
import { AnswerFragment, useGetAnswers, UserMinimalFragment } from '../../graphql/generated/definitions';
import useDateFnsLocale from "../../hooks/useDateFnsLocale";
import { dateFormatter } from "../../hooks/useFormatDate";
import useNormalizedSkillOptions from "../../hooks/useNormalizedSkillOptions";
import useTranslations from "../../hooks/useTranslations";
import useViewer from "../../hooks/useViewer";
import { getClosestGrade } from "../../utils/reviewUtils";
import {
  SortAscendingDirectionIcon,
  SortDescendingDirectionIcon,
} from "../icons";
import { Datum } from "../reviews/UserFollowUpAnalytics";
import TinyUserGradeOverTimeLineChart from "../analytics/charts/TinyUserGradeOverTimeLineChart";
import List from "../utils/List";
import SearchInput from "../utils/SearchInput";
import {
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardHeader,
  TailSpin,
} from "../wombat-ui";

import AddUserSkillModal from "./AddUserSkillModal";
import UserSkillSectionDetailModal from './UserSkillSectionDetailModal';

const SkillCardItem = ({
  answers,
  skillName,
  options,
  setDetailOpen,
  formatter,
  formattedDatum,
}) => {
  const t = useTranslations();

  const lastRatingAsReferent = useMemo(
    () =>
      (
        last(
          answers
            .filter((answer) => answer.collaborator.id !== answer.user.id)
            .sort((answer1, answer2) => (answer1.createdAt < answer2.createdAt ? -1 : 1)) || []
        ) as AnswerFragment
      )?.normalizedRating,
    [answers]
  );

  const lastRatingAsCollaborator = useMemo(
    () =>
      (
        last(
          answers
            .filter((answer) => answer.collaborator.id === answer.user.id)
            .sort((answer1, answer2) => (answer1.createdAt < answer2.createdAt ? -1 : 1)) || []
        ) as AnswerFragment
      )?.normalizedRating,
    [answers]
  );

  return (
    <Card
      className="overflow-auto"
      id={`skillCard${answers[0].question.skillId}`}
    >
      <CardHeader
        description={t("templates.lastUpdatedAt", {
          date: formatter(
            new Date(
              (last(answers) as AnswerFragment).createdAt
            ).getTime()
          ),
        })}
        className="truncate"
      >
        {skillName}
      </CardHeader>
      <CardBody className="flex-col">
        {answers.length > 1 ? (
          <TinyUserGradeOverTimeLineChart
            datum={formattedDatum(skillName)}
            options={options}
            scale={{
              grade: {
                range: [0, 1],
                ticks: options.map((option) => option.value),
              },
            }}
          />
        ) : (
          <div className="flex items-center justify-center text-sm h-52 text-light-900">
            {t("reviews.noEvolutionTrackedYet")}
          </div>
        )}
        <div className="grid grid-cols-2 pt-4 divide-x">
          <div className="flex flex-col items-center justify-center">
            <p className="text-sm text-light-900">{t("reviews.referents")}</p>
            <p className="text-lg font-bold">
              {lastRatingAsReferent !== undefined
                ? getClosestGrade(options, lastRatingAsReferent).label
                : t("users.skills.na")}
            </p>
          </div>
          <div className="flex flex-col items-center justify-center">
            <p className="text-sm text-light-900">{t("reviews.collaborator")}</p>
            <p className="text-lg font-bold">
              {lastRatingAsCollaborator !== undefined
                ? getClosestGrade(options, lastRatingAsCollaborator).label
                : t("users.skills.na")}
            </p>
          </div>
        </div>
        <Button
          className="pt-4"
          variant="link"
          scale="xs"
          block
          id={`${answers[0].question.skillId}-seeMore`}
          onClick={() => setDetailOpen({ open: true, key: skillName })}
        >
          {t("reviews.card.seeMore")}
        </Button>
      </CardBody>
    </Card>
  );
};

type UserSkillSectionProps = {
  user: UserMinimalFragment;
};

const UserSkillSection: React.FC<UserSkillSectionProps> = ({ user }) => {
  const t = useTranslations();
  const viewer = useViewer();
  const [addSkillModal, setAddSkillModal] = useToggle(false);
  const [detailOpen, setDetailOpen] = useState({
    open: false,
    key: null,
  });

  const dateFnsLocale = useDateFnsLocale();

  const options = useNormalizedSkillOptions();

  const [{ skillQ, direction }, setQuery] = useQueryParams({
    sortBy: StringParam,
    direction: StringParam,
    skillQ: StringParam,
  });

  const {
    data,
    loading,
    refetch: refetchAnswers,
  } = useGetAnswers({
    fetchPolicy: "network-only",
    variables: {
      sortBy: "skillName",
      direction: direction,
      skillQ,
      type: ["AnswerSkill"],
      collaboratorId: user.id,
    },
  });

  const [search, setSearch] = useState(skillQ);
  useEffect(() => setSearch(skillQ), [skillQ]);

  const formatter = useCallback(
    (v: string | number | Date) =>
      dateFormatter(
        // Bizcharts internally converts Date to timestamp string
        typeof v === "string" ? parseInt(v) : v,
        t(`dateFormat.short`) as string,
        t("dateFormat.fallback") as string,
        dateFnsLocale
      ),
    [dateFnsLocale]
  );

  const answers = useMemo(() => {
    if (!data?.answers?.collection) {
      return [];
    }

    return (
      groupBy(data?.answers?.collection || [], "question.skill.name")
    );
  }, [data?.answers?.collection]);

    const formattedDatum = useCallback((key: string) =>
      (answers[key]
        ? {
            data: sortBy(sortBy(answers[key], [(answer) => answer.createdAt]).map(
              (answer: AnswerFragment) => {
                return {
                  user: answer.user.name,
                  grade: answer.normalizedRating,
                  date: new Date(answer.createdAt).getTime(),
                  isCollaborator: answer.user.id === answer.collaborator.id,
                  isReferentAverage: false,
                };
              }
            ), [(a) => a.isCollaborator]),
            metadata: {
              title: key,
              question: answers[key][0].question,
            },
          }
        : {}) as Datum,
    [answers]
  );

  return (
    <>
      <List
        isLoading={loading}
        emptyMessage={t("users.skills.noSkills") as string}
        data={Object.keys(answers)}
        cardOnly
        cardPerRow={3}
        renderAsCard={(skillName) => (
          <SkillCardItem
            formatter={formatter}
            answers={answers[skillName]}
            key={skillName}
            skillName={skillName}
            options={options}
            setDetailOpen={setDetailOpen}
            formattedDatum={formattedDatum}
          />
        )}
        id="professions-list"
      >
        <div className="flex justify-between flex-grow pb-6 space-x-2">
          <SearchInput
            onSubmit={(skillQ) => setQuery({ skillQ: skillQ })}
            label={t("list.search") as string}
            placeholder={
              t("professionSkillLibrary.searchPlaceholder") as string
            }
            value={search}
            onChange={setSearch}
          />
          <div className="flex space-x-3">
            {viewer.isCapableOfCreatingStandaloneQuestions && (
              <Button
                id="addUserSkill"
                onClick={() => {
                  setAddSkillModal(true);
                }}
              >
                {t("users.skills.add")}
              </Button>
            )}
            <ButtonGroup scale="sm">
              <Button
                icon={SortAscendingDirectionIcon}
                onClick={() => setQuery({ direction: "ASC" })}
                variant={
                  direction === "ASC" || !direction ? "primary" : undefined
                }
              />
              <Button
                icon={SortDescendingDirectionIcon}
                onClick={() => setQuery({ direction: "DESC" })}
                variant={direction === "DESC" ? "primary" : undefined}
              />
            </ButtonGroup>
          </div>
        </div>
        {loading && (
          <div className="flex justify-center flex-grow">
            <TailSpin className="w-8 h-8" />
          </div>
        )}
      </List>
      <UserSkillSectionDetailModal
        refetch={refetchAnswers}
        datum={formattedDatum(detailOpen.key)}
        answers={answers}
        user={user}
        options={options}
        open={detailOpen.open}
        onClose={() => setDetailOpen({ open: false, key: null })}
      />
      {addSkillModal && (
        <AddUserSkillModal
          refetch={refetchAnswers}
          open={addSkillModal}
          onClose={() => setAddSkillModal(false)}
          userId={user.id}
        />
      )}
    </>
  );
};

export default React.memo(UserSkillSection);
